import { useState, useEffect, createContext, useContext, ReactNode, useCallback } from 'react';
import { Amplify } from 'aws-amplify';
import { signIn as amplifySignIn, signOut as amplifySignOut, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { awsConfig } from '../config/aws-config';
import { config } from '../config/environment';
import { initS3Client, destroyS3Client } from '../services/s3-upload';
import { autoSaveRegistry } from './useAutoSave';
import TwoFactorChallenge from '../components/auth/TwoFactorChallenge';
import ConfirmationBanner from '../components/auth/ConfirmationBanner';

// ── SSO Constants ──────────────────────────────────────────────────────
const COGNITO_DOMAIN = 'auth.easievaluation.com';
const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || '4oumqt74055lsp699fnn286s7r';

// HIPAA §164.312(a)(2)(iii): Inactivity timeout enforced across browser restarts.
// Must match the session timeout in SecurityWrapper / phi-protection (7 minutes).
const INACTIVITY_TIMEOUT_MS = 7 * 60 * 1000;

// Configure Amplify — on localhost with empty config, checkAuth() will
// fail gracefully and show the login screen (which is what we want).
try {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: awsConfig.cognito.userPoolId,
        userPoolClientId: awsConfig.cognito.clientId,
        identityPoolId: awsConfig.cognito.identityPoolId,
        loginWith: {
          oauth: {
            domain: COGNITO_DOMAIN,
            scopes: ['email', 'openid', 'profile'],
            redirectSignIn: [window.location.origin + '/'],
            redirectSignOut: [window.location.origin + '/login'],
            responseType: 'code',
          },
        },
      },
    },
  });
} catch {
  // Amplify config may fail with empty values on localhost — that's fine
}

interface User {
  userId: string;
  email: string;
  organizationId?: string;
}

// ── 2FA (Phase 1E Stage 2, Commit 4b) ─────────────────────────────────
// Default-off posture: all 2FA state starts 'idle' and only advances
// when config.features.twofaEnabled AND the ID token carries
// `custom:twofa_method=email_magic_link`. Flag-off = zero /2fa/ traffic.

type TwoFaStatus = 'idle' | 'pending' | 'verified' | 'expired' | 'failed';

export interface TwoFaState {
  status: TwoFaStatus;
  ticketId: string | null;
  expiresAt: number | null;
  error: string | null;
  email: string | null;
}

const TWOFA_ACK_LS_KEY = 'easi_twofa_ack';
const TWOFA_ACK_MAX_DISPLAYS = 3;
const TWOFA_TICKET_SS_KEY = 'easi_twofa_ticket';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<string | undefined>;
  signOut: () => Promise<void>;
  checkAuth: () => Promise<void>;
  // 2FA surface
  twofa: TwoFaState;
  verifyTwoFaCode: (code: string) => Promise<void>;
  resendTwoFaCode: () => Promise<void>;
  cancelTwoFa: () => Promise<void>;
  twofaAckShouldShow: boolean;
  markTwofaAckShown: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [twofa, setTwofa] = useState<TwoFaState>({
    status: 'idle',
    ticketId: null,
    expiresAt: null,
    error: null,
    email: null,
  });
  // Holds the freshly-issued ID token during the pending→verified window.
  // Not a React state leak of PHI: once setUser fires the token also ends
  // up in localStorage (same place it always has been).
  const [pendingIdToken, setPendingIdToken] = useState<string | null>(null);
  const [twofaAckShouldShow, setTwofaAckShouldShow] = useState(false);

  // ── 2FA helpers (internal) ─────────────────────────────────────────
  //
  // All /2fa/* network calls go through this module. Callers pass the
  // Cognito ID token explicitly so the caller's mental model stays clear:
  // before verification the token lives in pendingIdToken; after it's in
  // localStorage as easi_id_token. No reads-from-two-places ambiguity.

  const twofaRequest = useCallback(
    async (path: string, init: RequestInit, authToken: string) => {
      const response = await fetch(`${config.apiEndpoint}${path}`, {
        ...init,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
          ...(init.headers || {}),
        },
      });
      let json: any = {};
      try {
        json = await response.json();
      } catch {
        // Treat unparseable body as a transient error.
      }
      return { ok: response.ok && json?.success !== false, status: response.status, json };
    },
    [],
  );

  const startTwoFaChallenge = useCallback(
    async (idToken: string, email: string) => {
      const result = await twofaRequest('/2fa/initiate', { method: 'POST', body: '{}' }, idToken);
      if (!result.ok) {
        const err: any = new Error(result.json?.error?.message || 'Failed to start 2FA challenge');
        err.status = result.status;
        throw err;
      }
      const ticketId: string = result.json.data?.ticket_id;
      const expiresAt: number = result.json.data?.expires_at;
      if (!ticketId) {
        throw new Error('Malformed /2fa/initiate response');
      }
      try {
        sessionStorage.setItem(TWOFA_TICKET_SS_KEY, ticketId);
      } catch {
        // sessionStorage may throw in private mode — fall back to in-memory state.
      }
      setTwofa({ status: 'pending', ticketId, expiresAt, error: null, email });
    },
    [twofaRequest],
  );

  const shouldShowTwofaAck = useCallback((userSub: string): boolean => {
    try {
      const raw = localStorage.getItem(TWOFA_ACK_LS_KEY);
      if (!raw) return true;
      const parsed = JSON.parse(raw);
      if (parsed?.userSub !== userSub) return true;
      return (parsed?.displayCount || 0) < TWOFA_ACK_MAX_DISPLAYS;
    } catch {
      return true;
    }
  }, []);

  const finalizeTwoFaAuth = useCallback(async () => {
    if (!pendingIdToken) return;
    try {
      localStorage.setItem('easi_id_token', pendingIdToken);
      localStorage.setItem('easi_last_activity', Date.now().toString());
    } catch {
      // localStorage unavailable — callers downstream will re-auth.
    }
    try {
      initS3Client(pendingIdToken);
    } catch {
      // S3 client init failure is not fatal for auth completion.
    }
    let payload: any = {};
    try {
      payload = JSON.parse(atob(pendingIdToken.split('.')[1]));
    } catch {
      // Token parse fail — we still set user minimally.
    }
    const userSub = payload.sub || '';
    setUser({ userId: userSub, email: payload.email || '' });
    setTwofaAckShouldShow(shouldShowTwofaAck(userSub));
    try { sessionStorage.removeItem(TWOFA_TICKET_SS_KEY); } catch {}
    setPendingIdToken(null);
    setTwofa({ status: 'verified', ticketId: null, expiresAt: null, error: null, email: null });
  }, [pendingIdToken, shouldShowTwofaAck]);

  const verifyTwoFaCode = useCallback(
    async (code: string) => {
      const ticketId = (() => { try { return sessionStorage.getItem(TWOFA_TICKET_SS_KEY); } catch { return null; } })();
      const authToken = pendingIdToken || localStorage.getItem('easi_id_token');
      if (!ticketId || !authToken) {
        const err: any = new Error('NO_PENDING_CHALLENGE');
        err.status = 0;
        throw err;
      }
      const result = await twofaRequest(
        '/2fa/verify',
        {
          method: 'POST',
          body: JSON.stringify({ ticket_id: ticketId, code }),
        },
        authToken,
      );
      if (!result.ok) {
        const err: any = new Error(result.json?.error?.message || 'Verification failed');
        err.status = result.status;
        throw err;
      }
      await finalizeTwoFaAuth();
    },
    [pendingIdToken, twofaRequest, finalizeTwoFaAuth],
  );

  const resendTwoFaCode = useCallback(
    async () => {
      const ticketId = (() => { try { return sessionStorage.getItem(TWOFA_TICKET_SS_KEY); } catch { return null; } })();
      const authToken = pendingIdToken || localStorage.getItem('easi_id_token');
      if (!ticketId || !authToken) {
        const err: any = new Error('NO_PENDING_CHALLENGE');
        err.status = 0;
        throw err;
      }
      const result = await twofaRequest(
        '/2fa/resend',
        {
          method: 'POST',
          body: JSON.stringify({ ticket_id: ticketId }),
        },
        authToken,
      );
      if (!result.ok) {
        const err: any = new Error(result.json?.error?.message || 'Resend failed');
        err.status = result.status;
        throw err;
      }
      const expiresAt: number | undefined = result.json?.data?.expires_at;
      if (typeof expiresAt === 'number') {
        setTwofa((s) => ({ ...s, expiresAt, error: null }));
      }
    },
    [pendingIdToken, twofaRequest],
  );

  const cancelTwoFa = useCallback(async () => {
    try { sessionStorage.removeItem(TWOFA_TICKET_SS_KEY); } catch {}
    setPendingIdToken(null);
    setTwofa({ status: 'idle', ticketId: null, expiresAt: null, error: null, email: null });
    try {
      await amplifySignOut({ global: false });
    } catch {
      // May fail on localhost / no Cognito — that's fine.
    }
  }, []);

  const markTwofaAckShown = useCallback(() => {
    if (!user) return;
    try {
      const raw = localStorage.getItem(TWOFA_ACK_LS_KEY);
      let parsed: { userSub: string; displayCount: number; lastShownAt: number };
      if (raw) {
        const existing = JSON.parse(raw);
        if (existing?.userSub === user.userId) {
          parsed = {
            userSub: user.userId,
            displayCount: (existing.displayCount || 0) + 1,
            lastShownAt: Date.now(),
          };
        } else {
          parsed = { userSub: user.userId, displayCount: 1, lastShownAt: Date.now() };
        }
      } else {
        parsed = { userSub: user.userId, displayCount: 1, lastShownAt: Date.now() };
      }
      localStorage.setItem(TWOFA_ACK_LS_KEY, JSON.stringify(parsed));
    } catch {
      // Fail silently — banner will simply re-show next login.
    }
    setTwofaAckShouldShow(false);
  }, [user]);

  const checkAuth = useCallback(async () => {
    try {
      // ── HIPAA inactivity gate ──────────────────────────────────────
      // localStorage tokens survive "clear cache and history" in all browsers.
      // Without this gate, stale tokens silently re-authenticate users who
      // closed the browser hours/days ago. The 30-day Cognito refresh token
      // would keep them authenticated indefinitely.
      //
      // easi_last_activity is updated by SecurityWrapper on every user
      // interaction (mouse, keyboard, touch). If missing or >7 min stale,
      // the session is dead — clear everything and force re-login.
      const lastActivity = localStorage.getItem('easi_last_activity');
      const sessionExpired = !lastActivity ||
        (Date.now() - parseInt(lastActivity, 10)) > INACTIVITY_TIMEOUT_MS;

      if (sessionExpired) {
        // Wipe all auth tokens — do NOT attempt silent refresh
        localStorage.removeItem('easi_id_token');
        localStorage.removeItem('easi_access_token');
        localStorage.removeItem('easi_refresh_token');
        localStorage.removeItem('easi_last_activity');
        // Clear Amplify-managed tokens (dynamic key names)
        const amplifyKeys: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('CognitoIdentityServiceProvider.')) {
            amplifyKeys.push(key);
          }
        }
        amplifyKeys.forEach(k => localStorage.removeItem(k));
        setUser(null);
        setIsLoading(false);
        return;
      }

      // 1. Check for SSO tokens in localStorage first (survives page refresh)
      const ssoIdToken = localStorage.getItem('easi_id_token');
      const ssoAccessToken = localStorage.getItem('easi_access_token');
      if (ssoIdToken && ssoAccessToken) {
        try {
          const payload = JSON.parse(atob(ssoIdToken.split('.')[1]));
          const expiry = (payload.exp as number) * 1000;
          if (expiry > Date.now()) {
            // Token still valid — restore session without hitting Cognito
            initS3Client(ssoIdToken);
            setUser({
              userId: payload.sub || '',
              email: payload.email || '',
            });
            return;
          }
          // Token expired — try refresh below
        } catch {
          // Token parse failed — fall through
        }
      }

      // 2. Try refreshing with stored refresh_token
      const refreshToken = localStorage.getItem('easi_refresh_token');
      if (refreshToken) {
        try {
          const response = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
              grant_type: 'refresh_token',
              client_id: CLIENT_ID,
              refresh_token: refreshToken,
            }),
          });
          const tokens = await response.json();
          if (tokens.id_token) {
            localStorage.setItem('easi_id_token', tokens.id_token);
            localStorage.setItem('easi_access_token', tokens.access_token);
            if (tokens.refresh_token) {
              localStorage.setItem('easi_refresh_token', tokens.refresh_token);
            }
            const payload = JSON.parse(atob(tokens.id_token.split('.')[1]));
            initS3Client(tokens.id_token);
            setUser({ userId: payload.sub || '', email: payload.email || '' });
            return;
          }
        } catch (refreshErr: any) {
          // Distinguish network errors from genuine auth rejection.
          // Network errors (offline, DNS, timeout) should NOT destroy the session
          // — the SSO tokens may still be valid for API calls or the next refresh
          // attempt. Falling through to Amplify for SSO users always fails
          // (no Amplify session) → setUser(null) → ProtectedRoute unmounts
          // the entire component tree → all workflow state destroyed.
          const isNetworkError = refreshErr instanceof TypeError ||
            refreshErr?.message?.includes('Failed to fetch') ||
            refreshErr?.message?.includes('NetworkError') ||
            refreshErr?.message?.includes('Network request failed') ||
            refreshErr?.code === 'ERR_NETWORK' ||
            refreshErr?.code === 'ECONNABORTED';
          if (isNetworkError && ssoIdToken) {
            // Preserve existing session — tokens may be expired but the
            // refresh token is likely still valid. Next API call's 401
            // interceptor will retry the refresh with network-error awareness.
            console.warn('[EASI Auth] Network error during token refresh — preserving session');
            const payload = JSON.parse(atob(ssoIdToken.split('.')[1]));
            initS3Client(ssoIdToken);
            setUser({ userId: payload.sub || '', email: payload.email || '' });
            return;
          }
          // Non-network error (e.g. invalid_grant) — fall through to Amplify
        }
      }

      // 3. Fall back to Amplify session (email/password login).
      //    SSO users don't have an Amplify session — getCurrentUser() will throw,
      //    which is caught below. This is the correct path for SSO users whose
      //    refresh token was genuinely rejected (not a network error).
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();

      const idToken = session.tokens?.idToken?.toString();
      if (idToken) {
        localStorage.setItem('easi_id_token', idToken);
        initS3Client(idToken);
      }

      setUser({
        userId: currentUser.userId,
        email: currentUser.signInDetails?.loginId || '',
      });
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ── OAuth callback: exchange authorization code for tokens ─────────
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const oauthError = urlParams.get('error_description') || urlParams.get('error');

      // Handle OAuth errors (e.g. PreSignUp trigger rejection)
      if (oauthError) {
        // Strip Cognito's "PreSignUp failed with error " prefix to show our human-friendly message
        let cleanError = decodeURIComponent(oauthError.replace(/\+/g, ' '));
        const prefixMatch = cleanError.match(/^PreSignUp failed with error\s+/i);
        if (prefixMatch) {
          cleanError = cleanError.slice(prefixMatch[0].length);
        }
        sessionStorage.setItem('easi_oauth_error', cleanError);
        // Clean URL and redirect to login
        window.history.replaceState({}, document.title, '/login');
        setIsLoading(false);
        return;
      }

      if (!code) {
        // No OAuth callback — check existing session
        checkAuth();
        return;
      }

      try {
        const redirectUri = window.location.origin + '/';
        const response = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: CLIENT_ID,
            code: code,
            redirect_uri: redirectUri,
          }),
        });

        const tokens = await response.json();

        if (tokens.error) {
          console.error('OAuth token exchange failed:', tokens.error);
          setIsLoading(false);
          return;
        }

        // ── Method-lock / wrong-browser-session gate ────────────────
        // The `state` parameter Cognito echoed back was HMAC-signed by our
        // /auth/oauth-state endpoint and carries the email the user typed
        // on the login form. If the email inside the Google-issued ID
        // token doesn't match, the browser's Google session belongs to
        // someone other than the account being accessed — abort before
        // any token lands in localStorage.
        const returnedState = urlParams.get('state') || '';
        const idTokenPayload = JSON.parse(atob(tokens.id_token.split('.')[1]));
        const actualEmail = (idTokenPayload.email || '').toLowerCase().trim();

        // Every Commit A Google sign-in carries a signed state generated by
        // /auth/oauth-state. An OAuth callback without state can only come
        // from (a) a pre-Commit-A bookmarked URL that hits Cognito's OAuth
        // endpoint directly, or (b) an attacker constructing an OAuth URL
        // that bypasses the frontend gate. Both are rejected — fail closed.
        if (!returnedState) {
          sessionStorage.setItem(
            'easi_oauth_error',
            'Sign-in verification failed. Please click "Continue with Google" on the login page and try again.',
          );
          clearAllAuthState();
          window.history.replaceState({}, document.title, '/login');
          window.location.href = '/login';
          return;
        }

        try {
          const apiBase = process.env.REACT_APP_API_ENDPOINT || '';
          const vResp = await fetch(`${apiBase}/auth/validate-oauth-callback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              state: returnedState,
              actual_email: actualEmail,
            }),
          });
          const vJson = await vResp.json().catch(() => null);
          const vOk = vResp.ok && vJson?.data?.ok === true;
          if (!vOk) {
            const reason = vJson?.data?.reason || 'validation_failed';
            const expected = vJson?.data?.expected_email || '';
            const msg =
              reason === 'email_mismatch'
                ? `You signed in with a different Google account than the one you entered (${expected || 'expected email'}). Sign out of Google and try again.`
                : 'Your sign-in could not be verified. Please try again.';
            sessionStorage.setItem('easi_oauth_error', msg);
            // Do NOT store tokens. Clear URL and surface error via /login banner.
            clearAllAuthState();
            window.history.replaceState({}, document.title, '/login');
            window.location.href = '/login';
            return;
          }
        } catch (e) {
          // Fail closed — any error on the validation call aborts sign-in.
          console.error('OAuth callback validation error:', e);
          sessionStorage.setItem(
            'easi_oauth_error',
            'Sign-in verification failed. Please try again.',
          );
          clearAllAuthState();
          window.history.replaceState({}, document.title, '/login');
          window.location.href = '/login';
          return;
        }

        // Clear ALL old auth state before storing new tokens.
        // Prevents cross-account contamination when switching users on the same browser.
        clearAllAuthState();

        // Store tokens for API calls
        if (tokens.id_token) {
          localStorage.setItem('easi_id_token', tokens.id_token);
        }
        if (tokens.access_token) {
          localStorage.setItem('easi_access_token', tokens.access_token);
        }
        if (tokens.refresh_token) {
          localStorage.setItem('easi_refresh_token', tokens.refresh_token);
        }

        // Seed activity timestamp — checkAuth()'s inactivity gate requires this
        localStorage.setItem('easi_last_activity', Date.now().toString());

        // Parse user info from id_token JWT (already decoded above for validation)
        const payload = idTokenPayload;

        setUser({
          userId: payload.sub || '',
          email: payload.email || '',
        });

        if (tokens.id_token) {
          initS3Client(tokens.id_token);
        }

        // Clean the code from URL without reload
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (err) {
        console.error('OAuth callback error:', err);
        // Fall back to checking Amplify session
        await checkAuth();
      } finally {
        setIsLoading(false);
      }
    };

    handleOAuthCallback();
  }, [checkAuth]);

  /**
   * Refresh SSO tokens directly — bypasses the inactivity gate in checkAuth().
   * The proactive refresh must NEVER go through the inactivity gate because a
   * clinician watching a processing spinner is NOT idle — the system is working
   * on their behalf. The inactivity gate is only for browser restart scenarios.
   */
  const refreshTokenSilently = useCallback(async () => {
    // Try SSO refresh path (Google/Apple sign-in)
    const refreshToken = localStorage.getItem('easi_refresh_token');
    if (refreshToken) {
      try {
        const response = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            grant_type: 'refresh_token',
            client_id: CLIENT_ID,
            refresh_token: refreshToken,
          }),
        });
        const tokens = await response.json();
        if (tokens.id_token) {
          localStorage.setItem('easi_id_token', tokens.id_token);
          localStorage.setItem('easi_access_token', tokens.access_token);
          if (tokens.refresh_token) {
            localStorage.setItem('easi_refresh_token', tokens.refresh_token);
          }
          const payload = JSON.parse(atob(tokens.id_token.split('.')[1]));
          initS3Client(tokens.id_token);
          setUser({ userId: payload.sub || '', email: payload.email || '' });
          return;
        }
      } catch {
        // Network error — ignore, will retry on next API call via 401 interceptor
      }
    }
    // Fall back to Amplify refresh (email/password users)
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const idToken = session.tokens?.idToken?.toString();
      if (idToken) {
        localStorage.setItem('easi_id_token', idToken);
        initS3Client(idToken);
      }
    } catch {
      // Refresh failed — ignore, will retry on next API call
    }
  }, []);

  // Proactively refresh SSO tokens 5 minutes before expiry.
  // Uses refreshTokenSilently() instead of checkAuth() to bypass the
  // inactivity gate — a clinician watching a processing spinner is not idle.
  useEffect(() => {
    if (!user) return;
    const ssoIdToken = localStorage.getItem('easi_id_token');
    if (!ssoIdToken) return;
    try {
      const payload = JSON.parse(atob(ssoIdToken.split('.')[1]));
      const expiresAt = (payload.exp as number) * 1000;
      const refreshIn = expiresAt - Date.now() - 5 * 60 * 1000; // 5 min before expiry
      if (refreshIn <= 0) {
        refreshTokenSilently();
        return;
      }
      const timer = setTimeout(() => refreshTokenSilently(), refreshIn);
      return () => clearTimeout(timer);
    } catch {
      // ignore parse errors
    }
  }, [user, refreshTokenSilently]);

  /**
   * Clear ALL auth tokens from localStorage/sessionStorage.
   * MUST be called before any new sign-in to prevent cross-account contamination.
   * Without this, tokens from a previous session could cause User A to see User B's data.
   */
  function clearAllAuthState() {
    // Clear SSO tokens + activity timestamp
    localStorage.removeItem('easi_id_token');
    localStorage.removeItem('easi_access_token');
    localStorage.removeItem('easi_refresh_token');
    localStorage.removeItem('easi_last_activity');
    localStorage.removeItem('easi_remember_me');
    // Clear clinician-specific data that could leak across accounts
    localStorage.removeItem('easi_signature_block');
    // Clear ALL sessionStorage — patient data, views, transcripts, agreement status
    sessionStorage.clear();
    // Clear Amplify/Cognito SDK tokens (dynamic keys)
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('CognitoIdentityServiceProvider.')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
  }

  async function signIn(email: string, password: string): Promise<string | undefined> {
    // Clear stale tokens from any previous session to prevent cross-account data leakage
    clearAllAuthState();
    // Reset in-memory user state too — a fresh sign-in should never carry over a
    // prior user object. Without this, the 2FA pending path could report
    // isAuthenticated=true before verification (residual from an earlier checkAuth).
    setUser(null);
    const result = await amplifySignIn({ username: email, password });

    if (!result.isSignedIn) {
      // Surface challenge step to caller (e.g. NEW_PASSWORD_REQUIRED)
      return result.nextStep?.signInStep;
    }

    if (!config.features.twofaEnabled) {
      // Default-off posture: legacy behavior, zero /2fa/ traffic.
      localStorage.setItem('easi_last_activity', Date.now().toString());
      await checkAuth();
      return undefined;
    }

    // Flag on — inspect the ID token for `custom:twofa_method`. If absent
    // or not 'email_magic_link' (e.g. 'sso'), the user is not enrolled in
    // this 2FA method; fall through to the legacy sign-in.
    let claims: Record<string, any> = {};
    let idTokenString: string | undefined;
    try {
      const session = await fetchAuthSession();
      idTokenString = session.tokens?.idToken?.toString();
      claims = (session.tokens?.idToken?.payload as Record<string, any>) || {};
    } catch {
      // Amplify could not resolve the session — fall through to legacy.
    }

    if (claims['custom:twofa_method'] === 'email_magic_link' && idTokenString) {
      setPendingIdToken(idTokenString);
      const challengeEmail = String(claims.email || email).toLowerCase();
      try {
        await startTwoFaChallenge(idTokenString, challengeEmail);
      } catch (err) {
        // Initiate failed — unwind so the user returns to the login screen.
        setPendingIdToken(null);
        setTwofa({ status: 'failed', ticketId: null, expiresAt: null, error: (err as Error).message, email: null });
        try { await amplifySignOut({ global: false }); } catch {}
        throw err;
      }
      // Signal "success" to Login.tsx (no challenge step). The modal
      // owned by AuthProvider handles the remaining UX; isAuthenticated
      // stays false until finalizeTwoFaAuth runs.
      return undefined;
    }

    // No 2FA enrollment for this user — legacy completion.
    localStorage.setItem('easi_last_activity', Date.now().toString());
    await checkAuth();
    return undefined;
  }

  async function signOut() {
    // Flush all pending auto-saves before destroying the session (max 3s)
    await autoSaveRegistry.flushAll();
    // Clear ALL auth state (SSO tokens, Amplify tokens, session caches)
    clearAllAuthState();
    // Clear 2FA state too — a sign-out must never leave a dangling pending challenge
    try { sessionStorage.removeItem(TWOFA_TICKET_SS_KEY); } catch {}
    setPendingIdToken(null);
    setTwofa({ status: 'idle', ticketId: null, expiresAt: null, error: null, email: null });
    setTwofaAckShouldShow(false);
    // Destroy S3 client to prevent stale credential reuse across accounts
    destroyS3Client();

    try {
      // Global sign out revokes the refresh token on the Cognito server
      // so stolen tokens cannot be reused (HIPAA §164.312(a)(2)(iii))
      await amplifySignOut({ global: true });
    } catch {
      // May fail on localhost with no Cognito — that's fine
    }
    setUser(null);
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        signIn,
        signOut,
        checkAuth,
        twofa,
        verifyTwoFaCode,
        resendTwoFaCode,
        cancelTwoFa,
        twofaAckShouldShow,
        markTwofaAckShown,
      }}
    >
      {children}
      {/* Self-gating modal — renders only when signIn flipped status to 'pending'.
          With TWOFA_ENABLED=false, status stays 'idle' and this returns null. */}
      <TwoFactorChallenge />
      {/* Self-gating banner — null unless twofaAckShouldShow is true. */}
      <ConfirmationBanner />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
